import { useMemo } from 'react';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import { Icon } from '@/ui/icons';
import { SearchSelect } from '@/ui/SearchSelect';
import { distinct } from '@/data/aggregate';
import { isGroupable, prettyField } from '@/data/classify';
import { canFilterSheet, sheetFilterActive, type SheetFilterClause } from '@/model/sheetFilter';

/** The Value dropdown's own marker for "Visible only" — not a real layer name, so it never collides with one. */
const VISIBLE_SENTINEL = '__gt_visible_only__';

/**
 * The sheet's filter, above the grid — outside the cells, because it is not
 * something the sheet computes but what the sheet is ABOUT.
 *
 * Every GI formula and every database on the sheet applies it, so it has to be
 * impossible to miss: a sheet quietly showing one layer's numbers as though
 * they were the project's is the worst thing a filter can do. The bar is tinted
 * while anything is picked.
 */
export function FilterBar(): JSX.Element | null {
  useStore();
  const sheet = store.sheet();
  const data = store.data();
  const filter = sheet.sheetFilter;
  const fields = data.fields('areas');
  const choices = useMemo(() => fields.filter(isGroupable), [fields]);
  if (!filter || !canFilterSheet(sheet)) return null;

  const set = (i: number, patch: Partial<SheetFilterClause>): void =>
    store.setSheetFilter(filter.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const active = sheetFilterActive(sheet);

  return (
    <div className={`gt-filterbar${active ? ' is-active' : ''}`}>
      <span className="gt-filterbar-title" title="Applies to every GI formula and database on this sheet">
        <Icon.filter />
        {sheet.name} shows
      </span>
      {filter.map((c, i) => {
        const values = c.field ? distinct(data.rows('areas'), c.field).slice(0, 300).map(String) : [];
        // "Visible only" means the layer panel's eye icon — meaningless for
        // any other field, so only offered on the layer field itself.
        const isLayerField = c.field.toLowerCase() === 'layerid';
        const picked = c.op === 'visible' ? [VISIBLE_SENTINEL] : c.values;
        const isSet = c.op === 'visible' || c.values.length > 0;
        return (
          <span key={i} className={`gt-filterbar-clause${isSet ? ' is-set' : ''}`}>
            <SearchSelect
              value={c.field}
              title="Field"
              onChange={(field) => set(i, { field, values: [], op: undefined })}
              options={choices.map((f) => ({ value: f, label: prettyField(f) }))}
            />
            <SearchSelect
              value=""
              title="Value"
              placeholder="any"
              onChange={() => {}}
              multiValues={picked}
              onChangeMulti={(next) => {
                // Picking "Visible only" replaces any literal values (a moving
                // target and a fixed list don't combine); picking a literal
                // value drops "Visible only" the same way.
                if (next.includes(VISIBLE_SENTINEL) && !picked.includes(VISIBLE_SENTINEL)) {
                  set(i, { op: 'visible', values: [] });
                } else if (!next.includes(VISIBLE_SENTINEL) && picked.includes(VISIBLE_SENTINEL)) {
                  set(i, { op: undefined, values: [] });
                } else {
                  set(i, { op: undefined, values: next.filter((v) => v !== VISIBLE_SENTINEL) });
                }
              }}
              options={[
                ...(isLayerField ? [{ value: VISIBLE_SENTINEL, label: 'Visible only' }] : []),
                ...c.values
                  .filter((v) => !values.includes(v))
                  .map((v) => ({ value: v, label: `${v} (not in project)` })),
                ...values.map((v) => ({ value: v, label: v })),
              ]}
            />
            <button
              className="gt-btn"
              title="Remove"
              onClick={() => store.setSheetFilter(filter.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </span>
        );
      })}
      <button
        className="gt-btn"
        title="Filter by another field too"
        onClick={() => {
          const used = new Set(filter.map((c) => c.field));
          const next = choices.find((f) => !used.has(f)) ?? choices[0] ?? '';
          store.setSheetFilter([...filter, { field: next, values: [] }]);
        }}
      >
        + Field
      </button>
      <span className="gt-filterbar-note">
        {active ? 'Every GI formula and database on this sheet is filtered.' : 'Nothing picked — the sheet shows everything.'}
      </span>
      <button className="gt-btn" title="Take the filter off this sheet" onClick={() => store.setSheetFilter(undefined)}>
        Remove filter
      </button>
    </div>
  );
}
